import { Component } from '@angular/core';
import { Router } from '@angular/router';
import {
  CatalogDraft,
  CatalogStatus,
  CompletedCatalog,
} from '@core/interfaces/catalog.interface';
import { CatalogsService } from '@core/services/catalogs/catalogs.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-list-catalogs',
  templateUrl: './list-catalogs.component.html',
  styleUrls: ['./list-catalogs.component.scss'],
})
export class ListCatalogsComponent {
  public searchPhone = '';
  public currentPhone = '';

  public drafts: CatalogDraft[] = [];
  public draftsPage = 0;
  public draftsPageSize = 10;
  public draftsTotal = 0;

  public completed: CompletedCatalog[] = [];
  public completedPage = 0;
  public completedPageSize = 10;
  public completedTotal = 0;

  public draftColumns = [
    'business',
    'products',
    'template',
    'status',
    'updated',
    'action',
  ];
  public completedColumns = ['business', 'products', 'completed', 'action'];

  private statusLabels: Record<CatalogStatus, string> = {
    draft: 'Borrador',
    queued: 'En cola',
    generating: 'Generando',
    completed: 'Completado',
    failed: 'Fallido',
  };

  private templateLabels: Record<string, string> = {
    elegant: 'Elegante',
    modern: 'Moderno',
    visual: 'Visual',
  };

  constructor(
    public catalogsService: CatalogsService,
    private router: Router,
  ) {}

  search(): void {
    const phone = this.searchPhone.trim();
    if (!phone) return;
    this.currentPhone = phone;
    this.draftsPage = 0;
    this.completedPage = 0;
    this.loadDrafts();
    this.loadCompleted();
  }

  loadDrafts(): void {
    if (!this.currentPhone) return;
    this.catalogsService
      .listDrafts(this.currentPhone, this.draftsPage, this.draftsPageSize)
      .subscribe({
        next: (resp) => {
          this.drafts = resp.drafts || [];
          this.draftsTotal = resp.pagination?.total || 0;
        },
        error: () => {
          this.drafts = [];
          this.draftsTotal = 0;
        },
      });
  }

  loadCompleted(): void {
    if (!this.currentPhone) return;
    this.catalogsService
      .listCompleted(this.currentPhone, this.completedPage, this.completedPageSize)
      .subscribe({
        next: (resp) => {
          this.completed = resp.catalogs || [];
          this.completedTotal = resp.total || 0;
        },
        error: () => {
          this.completed = [];
          this.completedTotal = 0;
        },
      });
  }

  onDraftsPage(event: { pageIndex: number; pageSize: number }): void {
    this.draftsPage = event.pageIndex;
    this.draftsPageSize = event.pageSize;
    this.loadDrafts();
  }

  onCompletedPage(event: { pageIndex: number; pageSize: number }): void {
    this.completedPage = event.pageIndex;
    this.completedPageSize = event.pageSize;
    this.loadCompleted();
  }

  statusLabel(status: CatalogStatus): string {
    return this.statusLabels[status] || status;
  }

  templateLabel(template?: string): string {
    return (template && this.templateLabels[template]) || template || '—';
  }

  async newCatalog(): Promise<void> {
    let phone = (this.currentPhone || this.searchPhone).trim();

    if (!phone) {
      const { value } = await Swal.fire({
        title: 'Teléfono del dueño',
        input: 'text',
        inputPlaceholder: 'Ej: 573001234567',
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        inputValidator: (v) => (v && v.trim() ? null : 'Ingresa un teléfono'),
      });
      if (!value) return;
      phone = value.trim();
    }

    this.router.navigate(['/catalogs/create'], { queryParams: { phone } });
  }

  edit(draft: CatalogDraft): void {
    this.router.navigate(['/catalogs/edit', draft.draft_id], {
      queryParams: { phone: draft.phone || this.currentPhone },
    });
  }

  duplicate(draftId: string): void {
    this.catalogsService.duplicateDraft(draftId).subscribe({
      next: (resp) => {
        Swal.fire(
          'Duplicado',
          `Nuevo borrador con ${resp.products_count} producto(s).`,
          'success',
        );
        this.loadDrafts();
      },
      error: () =>
        Swal.fire('Error', 'No se pudo duplicar el catálogo.', 'error'),
    });
  }

  downloadPdf(url: string): void {
    if (url) window.open(url, '_blank');
  }

  share(draftId: string): void {
    this.catalogsService.shareCatalog(draftId).subscribe({
      next: (resp) => {
        Swal.fire({
          icon: 'success',
          title: 'Enlace público generado',
          html: `<p>Vigencia: ${resp.expiresIn}</p>
                 <input readonly value="${resp.publicUrl}"
                   style="width:100%;padding:8px;margin-top:8px;border:1px solid #ccc;border-radius:6px;" />`,
          confirmButtonText: 'Copiar',
          showCancelButton: true,
          cancelButtonText: 'Cerrar',
        }).then((r) => {
          if (r.isConfirmed && navigator.clipboard) {
            navigator.clipboard.writeText(resp.publicUrl);
          }
        });
      },
      error: () =>
        Swal.fire('Error', 'No se pudo generar el enlace público.', 'error'),
    });
  }
}
